import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlarmSeverityBadge } from "@/components/AlarmBadge";
import type { MachineDetail } from "@/features/lines/hooks";
import { useLineAttention } from "@/features/attention/hooks";
import { canJustify, occurrenceTitle } from "@/features/attention/occurrence-meta";
import { OccurrenceStatusPills } from "@/features/attention/OccurrenceStatusPills";
import { SuppressedAlarmsNote } from "@/features/attention/SuppressedAlarmsNote";
import type { AttentionOccurrence } from "@/types/attention.types";
import { AckAlarmInline, type AckAlarmTarget } from "./AckAlarmInline";
import { JustifyEventDialog } from "./JustifyEventDialog";
import { useAcknowledgeMachineEvent, useMachineEvents, type MachineEventSummary } from "./hooks";
import { EventStatusBadge } from "./EventStatusBadge";
import { Notify, toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { formatRelativeTime, getEventTypeLabel } from "@/lib/display-labels";

interface Props {
  machine: MachineDetail;
  lineId: string;
}

// A aba consome a MESMA visão de atenção unificada do card, filtrada
// para esta máquina, como fonte única de contagem. Alarme + parada
// da mesma ocorrência viram um item só, com ambos os badges. "Eventos
// recentes" abaixo é histórico informativo — NÃO conta como pendência.
// "Justificar" disponível em qualquer contexto (Dashboard ou detail),
// sem gate de papel, igual ao card "Atenção da linha" (operador também justifica;
// o backend não restringe). Desacoplado do `mode` da sidebar.
export function AlarmsTab({ machine, lineId }: Props) {
  const [ackTarget, setAckTarget] = useState<AckAlarmTarget | null>(null);
  const [justifyTarget, setJustifyTarget] = useState<string | null>(null);
  const [ackPendingId, setAckPendingId] = useState<string | null>(null);

  const attentionQuery = useLineAttention(lineId);
  const { data: attention } = attentionQuery;
  const eventsQuery = useMachineEvents(machine.id, 10);
  const { data: events = [] } = eventsQuery;
  const acknowledgeEvent = useAcknowledgeMachineEvent();

  const occurrences = (attention?.items ?? []).filter((o) => o.machineId === machine.id);
  const suppressed = attention?.summary.suppressedAlarms ?? 0;

  async function handleAcknowledgeEvent(eventId: string) {
    setAckPendingId(eventId);
    try {
      await acknowledgeEvent.mutateAsync({ eventId });
      toast.success("Evento reconhecido.");
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao reconhecer evento.", { description: msg });
    } finally {
      setAckPendingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <section>
        <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          Atenção nesta máquina ({occurrences.length})
        </h3>
        {attentionQuery.isError ? (
          <Notify.QueryError
            error={attentionQuery.error}
            onRetry={() => void attentionQuery.refetch()}
          />
        ) : occurrences.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nada exige atenção nesta máquina.</p>
        ) : (
          <ul className="space-y-2">
            {occurrences.map((occ) => (
              <OccurrenceRow
                key={occ.occurrenceId}
                occ={occ}
                ackPendingId={ackPendingId}
                onAckAlarm={setAckTarget}
                onAckEvent={(id) => void handleAcknowledgeEvent(id)}
                onJustify={setJustifyTarget}
              />
            ))}
          </ul>
        )}
        <SuppressedAlarmsNote count={suppressed} className="mt-2" />
      </section>

      <section>
        <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
          Eventos recentes ({events.length})
        </h3>
        {eventsQuery.isError ? (
          <Notify.QueryError error={eventsQuery.error} onRetry={() => void eventsQuery.refetch()} />
        ) : events.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sem eventos recentes.</p>
        ) : (
          <ul className="space-y-1">
            {events.map((e: MachineEventSummary) => (
              <li key={e.id} className="space-y-0.5 rounded border px-2 py-1.5 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium">{getEventTypeLabel(e.type)}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatRelativeTime(e.startAt)}
                    {e.durationMinutes !== null ? ` · ${e.durationMinutes} min` : null}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <EventStatusBadge
                    acknowledgedAt={e.acknowledgedAt}
                    reasonStatus={e.reasonStatus}
                  />
                </div>
                {e.reason ? (
                  <p className="text-xs text-muted-foreground">Motivo: {e.reason}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <AckAlarmInline alarm={ackTarget} open={!!ackTarget} onClose={() => setAckTarget(null)} />
      <JustifyEventDialog
        eventId={justifyTarget}
        open={!!justifyTarget}
        onClose={() => setJustifyTarget(null)}
      />
    </div>
  );
}

function OccurrenceRow({
  occ,
  ackPendingId,
  onAckAlarm,
  onAckEvent,
  onJustify,
}: {
  occ: AttentionOccurrence;
  ackPendingId: string | null;
  onAckAlarm: (t: AckAlarmTarget) => void;
  onAckEvent: (eventId: string) => void;
  onJustify: (eventId: string) => void;
}) {
  const alarmUnack = occ.alarm?.state === "UNACKNOWLEDGED";
  const justification = occ.justification;
  const justPending = canJustify(occ);
  return (
    <li className="space-y-1 rounded-md border bg-muted/10 p-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium">{occurrenceTitle(occ)}</p>
        {occ.alarm ? <AlarmSeverityBadge severity={occ.alarm.severity} /> : null}
      </div>
      <p className="text-xs text-muted-foreground">{occ.action}</p>
      <div className="flex flex-wrap items-center gap-1">
        <OccurrenceStatusPills occ={occ} />
      </div>
      <div className="flex justify-end gap-1 pt-0.5">
        {occ.alarm && alarmUnack ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onAckAlarm({
                id: occ.alarm!.refId,
                title: occ.alarm!.title,
                expectedAction: occ.action,
              })
            }
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Reconhecer alarme
          </Button>
        ) : null}
        {justPending && justification ? (
          <>
            {justification.acknowledgedAt === null ? (
              <Button
                size="sm"
                variant="outline"
                disabled={ackPendingId === justification.refId}
                onClick={() => onAckEvent(justification.refId)}
              >
                {ackPendingId === justification.refId ? "Reconhecendo…" : "Reconhecer"}
              </Button>
            ) : null}
            <Button size="sm" variant="outline" onClick={() => onJustify(justification.refId)}>
              Justificar
            </Button>
          </>
        ) : null}
      </div>
    </li>
  );
}
